import { useEffect, useState } from 'react';
import { Button, Card, Empty, Modal, Space, Spin, Typography } from 'antd';
import OrderService from 'services/order';
import { formatDate } from 'utils/date';
import { formatCurrency } from 'utils/number';
import { useOrderDetailScreen } from './Detail';
import OrderSnapshotModal from './OrderSnapshotModal';
import './table.css';

const { Text } = Typography;
const service = new OrderService();

const PAYMENT_STATUS_TITLES = {
    unpaid: 'Chờ thanh toán',
    partial: 'Thanh toán 1 phần',
    paid: 'Đã thanh toán',
    waiting_verification: 'Chờ xác nhận thanh toán',
};

const SHIPPING_STATUS_TITLES = {
    not_shipped: 'Chưa vận chuyển',
    shipping: 'Đang vận chuyển',
    shipped: 'Đã giao',
};

const ORDER_STATUS_TITLES = {
    canceled: 'Đã hủy',
    finalized: 'Hoàn tất',
    new: 'Mới',
};

const ACTION_TITLES = {
    order_created: 'Tạo đơn hàng',
    order_updated: 'Cập nhật đơn hàng',
    order_canceled: 'Hủy đơn hàng',
    order_finalized: 'Hoàn tất đơn hàng',
    order_bulk_update_shipping: 'Cập nhật trạng thái vận chuyển hàng loạt',
    order_bulk_update_internal: 'Cập nhật trạng thái nội bộ hàng loạt',
    order_note_added: 'Thêm ghi chú',
    order_note_updated: 'Cập nhật ghi chú',
    order_note_deleted: 'Xóa ghi chú',
};

const formatAddress = (address) => {
    if (!address || typeof address !== 'object') {
        return '-';
    }

    const fullName = address.full_name || '';
    const phone = address.phone_number || '';
    const detail = address.address || '';
    const parts = [fullName, phone, detail].filter(Boolean);
    return parts.length ? parts.join(' · ') : '-';
};

const formatLineItems = (items = []) => {
    if (!Array.isArray(items) || items.length === 0) {
        return '-';
    }

    return items
        .map(item => {
            const product = item?.product_name || item?.name || item?.product_id || 'Sản phẩm';
            const quantity = Number(item?.quantity || 0);
            const price = Number(item?.price || 0);
            return `${product} x ${quantity} (${formatCurrency(price)})`;
        })
        .join(' | ');
};

const formatStatus = (statusMap, value) => {
    if (!value) {
        return '-';
    }
    return statusMap[value] || value;
};

const formatInternalStatus = (value, entry = {}) => {
    if (!value) {
        return '-';
    }

    const statusMap = entry?.snapshot?.store_setting?.order_internal_status_map || {};
    if (statusMap[value]?.title) {
        return statusMap[value].title;
    }

    return value;
};

const formatValue = (field, value, entry = {}) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    if (typeof value === 'boolean') {
        return value ? 'Có' : 'Không';
    }

    if (field === 'discount_amount' || field === 'shipping_amount' || field === 'point_discount') {
        return formatCurrency(Number(value || 0));
    }

    if (field === 'loyalty_point') {
        return `${Number(value || 0)} điểm`;
    }

    if (field === 'shipping_address') {
        return formatAddress(value);
    }

    if (field === 'line_items') {
        return formatLineItems(value);
    }

    if (field === 'payment_status_id') {
        return formatStatus(PAYMENT_STATUS_TITLES, value);
    }

    if (field === 'shipping_status_id') {
        return formatStatus(SHIPPING_STATUS_TITLES, value);
    }

    if (field === 'status') {
        return formatStatus(ORDER_STATUS_TITLES, value);
    }

    if (field === 'internal_status_id') {
        return formatInternalStatus(value, entry);
    }

    if (field === 'customer_id') {
        return value;
    }

    if (Array.isArray(value) || typeof value === 'object') {
        return JSON.stringify(value);
    }

    return String(value);
};

const OrderHistoryModal = ({ open, onClose }) => {
    const { order } = useOrderDetailScreen();
    const [loading, setLoading] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [snapshotViewer, setSnapshotViewer] = useState({ open: false, title: '', snapshot: null });

    useEffect(() => {
        const loadHistory = async () => {
            if (!open || !order?.id) {
                return;
            }

            setLoading(true);
            try {
                const history = await service.getEditHistory({ id: order.id });
                setHistoryData(Array.isArray(history) ? history : []);
            } finally {
                setLoading(false);
            }
        };

        loadHistory();
    }, [open, order?.id]);

    const openSnapshotViewer = (title, snapshot) => {
        setSnapshotViewer({ open: true, title, snapshot: snapshot || null });
    };

    const closeSnapshotViewer = () => {
        setSnapshotViewer({ open: false, title: '', snapshot: null });
    };

    return (
        <>
            <Modal
                title="Lịch sử chỉnh sửa đơn hàng"
                open={open}
                onCancel={onClose}
                footer={null}
                width={900}
            >
                <Spin spinning={loading}>
                    {!historyData?.length && <Empty description="Chưa có lịch sử chỉnh sửa" />}

                    <Space direction="vertical" size={12} style={{ width: '100%' }}>
                        {(historyData || []).map((entry, index) => {
                            const beforeSnapshot = historyData?.[index + 1]?.snapshot || null;
                            const afterSnapshot = entry?.snapshot || null;

                            return (
                                <Card key={entry?.id || entry?.create_on} size="small" type="inner">
                                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                                        <div>
                                            <Text strong>{entry?.create_by?.full_name || entry?.create_by?.id || 'Người dùng'}</Text>
                                            <Text type="secondary"> · {formatDate(entry?.create_on)}</Text>
                                        </div>

                                        <Text type="secondary">{ACTION_TITLES[entry?.action] || 'Cập nhật đơn hàng'}</Text>

                                        <Space size={8} wrap>
                                            <Button
                                                size="small"
                                                disabled={!beforeSnapshot}
                                                onClick={() => openSnapshotViewer('Đơn hàng trước thay đổi', beforeSnapshot)}
                                            >
                                                Xem đơn hàng trước thay đổi
                                            </Button>
                                            <Button
                                                size="small"
                                                onClick={() => openSnapshotViewer('Đơn hàng sau thay đổi', afterSnapshot)}
                                            >
                                                Xem đơn hàng sau thay đổi
                                            </Button>
                                        </Space>

                                        <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                            {(entry?.changes || []).length === 0 ? (
                                                <Text type="secondary">Không có thay đổi chi tiết</Text>
                                            ) : (
                                                (entry?.changes || []).map((change, changeIndex) => (
                                                    <div key={`${entry?.id || 'history'}-${change?.field || changeIndex}`}>
                                                        {change?.description ? (
                                                            <Text>{change.description}</Text>
                                                        ) : (
                                                            <>
                                                                <Text strong>{change?.label || change?.field}: </Text>
                                                                <Text type="secondary">{formatValue(change?.field, change?.before, entry)}</Text>
                                                                <Text> → </Text>
                                                                <Text>{formatValue(change?.field, change?.after, entry)}</Text>
                                                            </>
                                                        )}
                                                    </div>
                                                ))
                                            )}
                                        </Space>
                                    </Space>
                                </Card>
                            );
                        })}
                    </Space>
                </Spin>
            </Modal>

            <OrderSnapshotModal
                title={snapshotViewer.title}
                open={snapshotViewer.open}
                snapshot={snapshotViewer.snapshot}
                onClose={closeSnapshotViewer}
            />
        </>
    );
};

export default OrderHistoryModal;